import { ChangeEvent, ElementType, memo, useCallback, useEffect, useState } from 'react';
import { Button, ButtonProps } from 'src/common/Button/Button';
import { MinusIcon } from 'src/modules/common/icons/MinusIcon';
import { PlusIcon } from 'src/modules/common/icons/PlusIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon.tsx';

type Props = {
    value?: number;
    onChange?: (newCount: number) => void;
    onDelete?: () => void;
    size?: ButtonProps<ElementType>['size'];
}

export const Counter = memo(({ value, onChange, onDelete, size = 'md' }: Props) => {
    const [count, setCount] = useState(1);
    // raw input text while the user is typing; null means "show count"
    const [draft, setDraft] = useState<string | null>(null);

    const handleClick = (newValue: number) => {
        if (count === 1 && newValue <= count && onDelete) {
            onDelete();
            return;
        }
        const newCount = newValue < 1 ? 1 : newValue;
        setDraft(null);
        setCount(newCount);
        onChange?.(newCount);
    };

    const handleInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setDraft(event.target.value);
        const newCount = parseInt(event.target.value, 10);
        // empty / non-numeric input gives NaN, which JSON.stringify turns into null;
        // keep it only as draft so the user can clear the field and type a new number
        if (Number.isNaN(newCount) || newCount < 1) {
            return;
        }
        setCount(newCount);
        onChange?.(newCount);
    }, [onChange]);

    const handleBlur = useCallback(() => setDraft(null), []);

    useEffect(() => {
        if (value !== count) {
            setCount(value || 1);
        }
    }, [count, value]);

    return (
        <div className="vss-counter">
            <Button
                size={size}
                iconOnly={true}
                kind="ghost-secondary"
                onClick={() => handleClick(count - 1)}
                className="vss-counter__button"
                disabled={!onChange}
            >
                <div className="vss-counter__icon">
                    {onDelete && count === 1 ? <DeleteIcon/> : <MinusIcon/>}
                </div>
            </Button>

            <span className="vss-counter__count">
                <input
                    type="number"
                    min="1"
                    step="1"
                    value={draft ?? count}
                    onChange={handleInput}
                    onBlur={handleBlur}
                />
            </span>

            <Button
                size={size}
                iconOnly={true}
                kind="ghost-secondary"
                onClick={() => handleClick(count + 1)}
                className="vss-counter__button"
                disabled={!onChange}
            >
                <div className="vss-counter__icon">
                    <PlusIcon/>
                </div>
            </Button>
        </div>
    );
});
